import React, { useState } from 'react';
import axios from 'axios';
import { Formik, setIn } from 'formik';
import { capitalize, isEmpty } from 'lodash';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import { getAllSearchableContactRoles } from '~/components/communications/ContactUtils';
import ContactTextFieldFormik from '~/components/ContactTextFieldFormik';
import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import RadioButtonGroupFormik from '~/components/core/Formik/RadioButtonGroupFormik';
import { usePaymentsConfiguration } from '~/components/hooks/usePaymentsConfiguration';
import { useVatLabel } from '~/components/hooks/useVatLabel';
import { PreDefinedField } from '~/components/IncidentConfiguration/ConfiguredFields';
import useOrganization from '~/components/OrganizationContext';
import type {
  PickedRequestAllocation,
  SettlesRecoveryRequestValue,
} from '~/components/RecoveryRequests/SettlesRecoveryRequestField';
import SettlesRecoveryRequestField, {
  RECOVERED_FROM_CONTACT_FIELD_ID,
  SETTLES_RECOVERY_REQUEST_FIELD_ID,
} from '~/components/RecoveryRequests/SettlesRecoveryRequestField';
import TextFieldFormik, { MonetaryValueTextFieldFormik } from '~/components/TextFieldFormik';
import { reportAxiosError } from '~/Utils';
import { extractError } from '~/Utils/errors/extractError';

import Caption from '../core/TextComponents/Caption';

import { CapBlockedModal, CapWarningModal } from './CapRecoveryModals';
import VatFormikFields, { getVatValidationSchema } from './VatFormikFields';

interface SingleGeneralRecoveryDialogProps {
  claimId: number;
  exposureIdOrNullIfGeneralExpenses: number | null;
  onClose: () => void;
  onUpdate: () => void | Promise<void>;
}

interface SingleGeneralRecoveryFormSubmitValuesProps {
  payable_type: string;
  amount: string;
  recovered_from_contact_id: string | number;
  reference: string;
  check_number: string;
  note: string;
  general_recovery_type?: string;
  is_vat_included?: boolean | string;
  vat_amount?: string;
  settles_recovery_request_id?: SettlesRecoveryRequestValue;
}

interface CapWarningState {
  url: string;
  payload: Record<string, unknown>;
  message: string;
}

// The pre-split-recovery Add Recovery dialog: one payable type, one amount, posting to the
// legacy general_recoveries endpoints. Rendered when the split-general-recovery flag is off.
const SingleGeneralRecoveryDialog: React.FC<SingleGeneralRecoveryDialogProps> = ({
  claimId,
  exposureIdOrNullIfGeneralExpenses,
  onClose,
  onUpdate,
}) => {
  const { classes } = useStyles();
  const [capWarning, setCapWarning] = useState<CapWarningState | null>(null);
  const [capBlockedMessage, setCapBlockedMessage] = useState<string | null>(null);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organizationContactRolesDict } = useOrganization();
  const { generalRecoveriesConfiguration, includeVatInPaymentScreen } = usePaymentsConfiguration();
  const vatLabel = useVatLabel();

  // The payable type and the recovery type are the request's own once one is picked. The settlement
  // link demands the recovery sit in the allocation the request named, so a radio left on the other
  // one would roll the whole action back on a 400; the recovery type is only a prefill, and stays
  // the adjuster's to change.
  const applyPickedRequest = React.useCallback(
    (values: Record<string, unknown>, { payableType, generalRecoveryType }: PickedRequestAllocation) => {
      const nextValues = setIn(values, 'payable_type', payableType);
      return generalRecoveryType ? setIn(nextValues, 'general_recovery_type', generalRecoveryType) : nextValues;
    },
    []
  );

  const buildPayload = (values: SingleGeneralRecoveryFormSubmitValuesProps): Record<string, unknown> => {
    const payload: Record<string, unknown> = {
      amount: values.amount,
      recovered_from_contact_id: values.recovered_from_contact_id,
      reference: values.reference,
      check_number: values.check_number,
      note: values.note,
    };

    if (!isEmpty(values?.general_recovery_type) && generalRecoveriesConfiguration?.general_recovery_type?.active) {
      payload.general_recovery_type = values.general_recovery_type;
    }

    if (includeVatInPaymentScreen) {
      payload.is_vat_included = values.is_vat_included;
      if (values.is_vat_included) {
        payload.vat_amount = values.vat_amount;
      }
    }

    // Left out entirely when unset, so an adjuster who ignores the field posts exactly the body they
    // post today. Set on the built payload rather than at the call site so the cap-override re-post,
    // which re-sends this very object, carries the link too.
    if (values.settles_recovery_request_id) {
      payload[SETTLES_RECOVERY_REQUEST_FIELD_ID] = values.settles_recovery_request_id;
    }

    return payload;
  };

  const buildUrl = (values: SingleGeneralRecoveryFormSubmitValuesProps): string =>
    exposureIdOrNullIfGeneralExpenses
      ? `/api/v1/claims/${claimId}/exposures/${exposureIdOrNullIfGeneralExpenses}/${values.payable_type}/general_recoveries`
      : `/api/v1/claims/${claimId}/general_expenses/general_recoveries`;

  const handleSubmit = async (
    values: SingleGeneralRecoveryFormSubmitValuesProps,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    const url = buildUrl(values);
    const payload = buildPayload(values);
    try {
      await axios.post(url, payload);
      await onUpdate();
      onClose();
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 409) {
          setCapWarning({ url, payload, message: extractError(error).message });
          setSubmitting(false);
          return;
        }
        if (error.response?.status === 422) {
          setCapBlockedMessage(extractError(error).message);
          setSubmitting(false);
          return;
        }
      }
      await reportAxiosError(error);
      setSubmitting(false);
    }
  };

  const handleCapConfirm = async (reason: string): Promise<void> => {
    if (!capWarning) return;
    try {
      await axios.post(capWarning.url, { ...capWarning.payload, cap_override_reason: reason });
      setCapWarning(null);
      await onUpdate();
      onClose();
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };

  return (
    <>
      {capBlockedMessage !== null && (
        <CapBlockedModal message={capBlockedMessage} onClose={() => setCapBlockedMessage(null)} />
      )}
      {capWarning && (
        <CapWarningModal
          message={capWarning.message}
          onConfirm={handleCapConfirm}
          onClose={() => setCapWarning(null)}
        />
      )}
      <Formik
        initialValues={{
          payable_type: exposureIdOrNullIfGeneralExpenses === null ? 'expenses' : '',
          amount: '',
          recovered_from_contact_id: '',
          reference: '',
          check_number: '',
          note: '',
          general_recovery_type: '',
          is_vat_included: '' as unknown as boolean,
          vat_amount: '',
          settles_recovery_request_id: '' as SettlesRecoveryRequestValue,
        }}
        validationSchema={Yup.object().shape({
          payable_type: Yup.string().required('Required'),
          amount: Yup.number().nullable().required('Required'),
          recovered_from_contact_id: Yup.number().required('Required'),
          reference: Yup.string(),
          note: Yup.string(),
          general_recovery_type: generalRecoveriesConfiguration?.general_recovery_type?.mandatory
            ? Yup.string().required('Required')
            : Yup.string(),
          ...getVatValidationSchema(vatLabel, !!includeVatInPaymentScreen),
        })}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {(formikProps) => {
          const { isSubmitting, handleSubmit } = formikProps;

          return (
            <CardDialog
              open
              isDialog
              fullWidth
              maxWidth="sm"
              onClose={onClose}
              title="Add Recovery Amount"
              preventClose={isSubmitting}
            >
              <div className="grid gap-12">
                {/* First, because it determines every field below it: picking a request fixes the
                    payable type and the payer, and prefills the recovery type. */}
                <SettlesRecoveryRequestField
                  exposureIdOrNullIfGeneralExpenses={exposureIdOrNullIfGeneralExpenses}
                  applyPickedRequest={applyPickedRequest}
                  disabled={isSubmitting}
                />

                <Caption variant={Caption.VARIANTS.LABEL}>Payable Type</Caption>
                <RadioButtonGroupFormik
                  id="payable_type"
                  options={['indemnity', 'expenses'].map((payableType) => ({
                    optionValue: payableType,
                    text: capitalize(payableType),
                  }))}
                  showOnly={
                    isSubmitting ||
                    exposureIdOrNullIfGeneralExpenses === null ||
                    !!formikProps.values.settles_recovery_request_id
                  }
                />

                <div className="w-1/2">
                  <MonetaryValueTextFieldFormik
                    id="amount"
                    label="Amount"
                    className={classes.textField}
                    disabled={isSubmitting}
                    fullWidth
                  />
                </div>
                {includeVatInPaymentScreen && <VatFormikFields disabled={isSubmitting} />}
                <div className="w-1/2">
                  {/* Not a free choice while a request is named: `settle()` attributes the money to
                      the request's own payer, so any other contact would record this recovery
                      against a party the request never named. Clearing the request frees it again. */}
                  <ContactTextFieldFormik
                    id={RECOVERED_FROM_CONTACT_FIELD_ID}
                    label="Recovered from Contact"
                    acceptedRoles={getAllSearchableContactRoles(organizationContactRolesDict)}
                    fixedSearchResults
                    fullWidth
                    disabled={isSubmitting}
                    showOnly={!!formikProps.values.settles_recovery_request_id}
                  />
                </div>
                <div>
                  <TextFieldFormik
                    id="reference"
                    label="Reference"
                    className={classes.textField}
                    fullWidth
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <TextFieldFormik
                    id="check_number"
                    label="Check Number"
                    className={classes.textField}
                    fullWidth
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <TextFieldFormik
                    id="note"
                    label="Recovery Note"
                    className={classes.textField}
                    fullWidth
                    disabled={isSubmitting}
                  />
                </div>
                {generalRecoveriesConfiguration?.general_recovery_type?.active &&
                  !isEmpty(generalRecoveriesConfiguration?.general_recovery_type?.options) && (
                    <PreDefinedField
                      id="general_recovery_type"
                      fields={generalRecoveriesConfiguration}
                      inline
                      disabled={isSubmitting}
                    />
                  )}
              </div>

              <div className={classes.buttonsContainer}>
                <CancelButton content="Cancel" disabled={isSubmitting} onClick={onClose} />
                <Button variant="contained" color="primary" disabled={isSubmitting} onClick={() => handleSubmit()}>
                  Add
                </Button>
              </div>
            </CardDialog>
          );
        }}
      </Formik>
    </>
  );
};

export default SingleGeneralRecoveryDialog;
